import type {TabItem} from '@yandex-delivery/blocks/tabs/Tabs'
import type {Point} from '@yandex-delivery/core/types'
import {PickupStation} from '@yandex-delivery/types/logistic-dispatcher/external-api' // Убрать когда сделаем новую полноразмерную карту

// ======== fetchTariffsZones =========

export type TariffsZonesInfo = {
  countries: TariffsCountry[]
  zones: TariffsZones
  defaultCountry?: string
  getCurrentCountry: (countryId: string) => TariffsCountry
  getCurrentZones: (countryId: string) => ZonesInfo
  getCurrentCity: (countryId: string, cityId: string) => TariffsZone
  shouldAskCountry: boolean
}

export type TariffsCountry = {
  id: string
  title: string
}

export type TariffsZones = Record<TariffsCountry['id'], ZonesInfo>

export type ZonesInfo = {
  allZones: TariffsZone[]
  mainZones?: TariffsZone[]
}

export type TariffsZone = {
  id: string
  title: string
  point: Point
}

export type CitiesByChar = {
  cities: TariffsZone[]
  char?: string
}

// ======== fetchTariffsList =========
export type Tariff = {
  id: string
  title: string
  type: 'ndd' | 'cargo'
}

export type Message = {
  title: string
  subtitle?: string
  icon?: string
}

export type TariffData = {
  tariffs: Tariff[]
  messages: Message[]
  defaultTariff: Tariff
  currentTariff?: Tariff
  tabItems: TabItem<string>[]
}

export type TariffsList = {
  tariffs: Tariff[]
  messages: Message[]
  getCurrentTariff: (tariffId: string) => Tariff
  tabItems: TabItem<string>[]
  defaultTariff: Tariff
}

// =========== Информация о тарифе ===========

export type TariffsCargoInfo = {
  tariffsInfo: TariffsInfoByPaymentKind[]
  mapPolygon: Point[]
  filters: TariffFilter[]
}
export type TariffsNddInfo = {
  mapPolygon?: Point[]
  pickupStationsList?: PickupStation[]
}

export type TariffsInfoByPaymentKind = {
  paymentKind: string
  paymentKindTitle: string
  blocks: BlocksByColumns
  disableReason?: string
}

export type BlocksByColumns = {
  top: InfoBlock[]
  columns: InfoBlock[][]
}

// ======== Блоки =========

export type InfoBlock =
  | BlockWithoutValuesType
  | BlockWithRequirementsType
  | CommonBlockType
  | BlockWithCommonSubblocksType

export enum BlockTypes {
  BLOCK_WITHOUT_VALUES = 'block_without_values',
  BLOCK_WITH_REQUIREMENTS = 'block_with_requirements',
  COMMON_BLOCK = 'common_block',
  BLOCK_WITH_COMMON_SUBBLOCKS = 'block_with_common_subblocks',
}

export type BlockWithoutValuesType = {
  type: BlockTypes.BLOCK_WITHOUT_VALUES
  id: string
  title: string
  row: number
  column?: number // Если undefined, то рисуем сверху без колонок
  subtitle?: string
  mayBeCollapsed?: boolean
  text?: string
  action?: BlockWithoutValuesAction
  icon?: string
}

export type BlockWithoutValuesAction = {
  type: string
  permission?: string
}

export type BlockWithRequirementsType = {
  type: BlockTypes.BLOCK_WITH_REQUIREMENTS
  id: string
  title: string
  requirements: RequirementType[]
  row: number
  column?: number // Если undefined, то рисуем сверху без колонок
  subtitle?: string
  titleValue?: string
}

export type RequirementType = {
  id: string
  name: string
  value: string
}

export type CommonBlockType = {
  type: BlockTypes.COMMON_BLOCK
  id: string
  title: string
  subblocks: BlockWithRequirementsType[]
  row: number
  column?: number // Если undefined, то рисуем сверху без колонок
  subtitle?: string
}

export type BlockWithCommonSubblocksType = {
  type: BlockTypes.BLOCK_WITH_COMMON_SUBBLOCKS
  id: string
  title: string
  blocks: CommonBlockType[]
  row: number
  column?: number // Если undefined, то рисуем сверху без колонок
  subtitle?: string
}

export type TariffFiltersValue = Record<string, string>

export type TariffSingleChoiceFilterOption = {
  /**
   * Название опции фильтра для отображения
   */
  name: string
  /**
   * Значение опции фильтра
   */
  value: string
}

export type TariffSingleChoiceFilter = {
  /**
   * Идентификатор фильтра
   */
  id: string
  /**
   * Список опций фильтра
   */
  options: TariffSingleChoiceFilterOption[]
}

export type TariffFilter = TariffSingleChoiceFilter
